/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Collapse,
  Flex,
  Grid,
  Heading,
  IconButton,
  Spinner,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

import Card from "components/card/Card";
import Banner from "views/admin/profile/components/Banner";
import Storage from "views/admin/profile/components/Storage";
import Personal from "views/admin/profile/components/Personal";
import Identity from "views/admin/profile/components/Identity";
import Address from "views/admin/profile/components/Address";
import Business from "views/admin/profile/components/Business";
import Upload from "views/admin/profile/components/Upload";
import NotificationStack from "components/notification/NotificationStack";

import constants from "providers/Constants";
import useAuthentication from "providers/Authentication";

import banner from "assets/img/auth/banner.png";
import avatar from "assets/img/avatars/avatar4.png";
import Settings from "views/admin/profile/components/Settings";

export default function Overview() {
  const { t } = useTranslation();
  const authentication = useAuthentication();

  const [user, setUser] = useState({});
  const [profile, setProfile] = useState({});
  const [profile_score, setProfileScore] = useState({});
  const [developer, setDeveloper] = useState({});
  const [token, setToken] = useState({});
  const [countries, setCountries] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState('');

  const personalOpen = openSection === 'personal';
  const identityOpen = openSection === 'identity';
  const addressOpen = openSection === 'address';
  const businessOpen = openSection === 'business';
  const accountOpen = openSection === 'account';
  const uploadOpen = openSection === 'upload';

  function toggleSection(name) {
    setOpenSection(prev => (prev === name ? '' : name));
  }

  useEffect(() => {
    getCountries();
  }, []);

  const gridLayout = {
    mb: "20px",
    templateColumns: { base: "1fr", lg: "4fr 2fr" },
    templateRows: { base: "repeat(2, 1fr)", lg: "1fr" },
    gap: { base: "20px", xl: "20px" },
  };

  const cardBg = useColorModeValue("white", "gray.700");
  const hasNotifications = notifications && notifications.length > 0;

  const name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const job = user?.email || "";
  const apiKey = developer?.api_key || "";

  const scoreValue = Number(profile_score?.profile_score ?? NaN);
  const scoreUnit = profile_score?.unit || "%";
  const hasScore = Number.isFinite(scoreValue);
  const scoreScheme = hasScore
    ? scoreValue < 50
      ? "red"
      : scoreValue < 75
        ? "orange"
        : scoreValue < 90
          ? "yellow"
          : "green"
    : "gray";

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      {loading ? (
        <Center height="70vh">
          <Spinner size="xl" />
        </Center>
      ) : (
        <>
          {hasNotifications && (
            <NotificationStack
              notifications={notifications.map((message) => ({
                type: 'error',
                title: 'Action Required',
                message: message,
              }))}
            />
          )}

          <Grid {...gridLayout}>
            <Banner
              gridArea="1 / 1 / 2 / 2"
              banner={banner}
              avatar={user?.avatar || avatar}
              name={name}
              job={job}
              API_KEY={apiKey}
            />
            <Storage
              gridArea={{ base: "2 / 1 / 3 / 2", lg: "1 / 2 / 2 / 3" }}
              used={token?.available_token || 0}
              total={token?.max_token || 0}
            />
          </Grid>

          <Grid
            mb="20px"
            templateColumns={{ base: "1fr", lg: "4fr 2fr" }}
            gap={{ base: "20px", xl: "20px" }}
            alignItems="start"
          >
            {/* Left column: stacked sections */}
            <Box>
              {/* Personal */}
              <Card mb="20px">
                <Flex
                  align="center"
                  justify="space-between"
                  mb="2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={personalOpen}
                  onClick={() => toggleSection('personal')}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection('personal');
                    }
                  }}
                >
                  <Box>
                    <Heading size="md">{t("Personal Information")}</Heading>
                    <Text fontSize="sm" color="gray.500" pt="1">
                      {t("You are required to provide your personal information correctly.")}
                    </Text>
                  </Box>
                  <Flex align="center" gap={2}>
                    {!personalOpen && hasScore && (
                      <Tag size="sm" colorScheme={scoreScheme} borderRadius="full" aria-label={t("Profile Score")}>
                        {`${scoreValue}${scoreUnit || ""}`}
                      </Tag>
                    )}
                    <IconButton
                      aria-label={
                        personalOpen
                          ? t("Collapse {{section}} section", { section: t("Personal Information") })
                          : t("Expand {{section}} section", { section: t("Personal Information") })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection('personal');
                      }}
                      icon={personalOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
                <Collapse in={personalOpen} animateOpacity>
                  <Box mt={4} minH="365px">
                    {loading ? (
                      <Center minH="365px" w="100%">
                        <Spinner size="xl" />
                      </Center>
                    ) : (
                      <Personal user={user} profile={profile} developer={developer} minH="365px" />
                    )}
                  </Box>
                </Collapse>
              </Card>

              {/* Identity */}
              <Card mb="20px">
                <Flex
                  align="center"
                  justify="space-between"
                  mb="2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={identityOpen}
                  onClick={() => toggleSection('identity')}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection('identity');
                    }
                  }}
                >
                  <Box>
                    <Heading size="md">{t("Identity Information")}</Heading>
                    <Text fontSize="sm" color="gray.500" pt="1">
                      {t("Verify your identity to start performing transactions without restriction")}
                    </Text>
                  </Box>
                  <Flex align="center" gap={2}>
                    {!identityOpen && hasScore && (
                      <Tag size="sm" colorScheme={scoreScheme} borderRadius="full" aria-label={t("Profile Score")}>
                        {`${scoreValue}${scoreUnit || ""}`}
                      </Tag>
                    )}
                    <IconButton
                      aria-label={
                        identityOpen
                          ? t("Collapse {{section}} section", { section: t("Identity Information") })
                          : t("Expand {{section}} section", { section: t("Identity Information") })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection('identity');
                      }}
                      icon={identityOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
                <Collapse in={identityOpen} animateOpacity>
                  <Box mt={4} minH="365px">
                    {loading ? (
                      <Center minH="365px" w="100%">
                        <Spinner size="xl" />
                      </Center>
                    ) : (
                      <Identity user={user} profile={profile} countries={countries} uploads={uploads} minH="365px" />
                    )}
                  </Box>
                </Collapse>
              </Card>

              {/* Address */}
              <Card mb="20px">
                <Flex
                  align="center"
                  justify="space-between"
                  mb="2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={addressOpen}
                  onClick={() => toggleSection('address')}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection('address');
                    }
                  }}
                >
                  <Box>
                    <Heading size="md">{t("Address Information")}</Heading>
                    <Text fontSize="sm" color="gray.500" pt="1">
                      {t("Provide your address details")}
                    </Text>
                  </Box>
                  <Flex align="center" gap={2}>
                    {!addressOpen && hasScore && (
                      <Tag size="sm" colorScheme={scoreScheme} borderRadius="full" aria-label={t("Profile Score")}>
                        {`${scoreValue}${scoreUnit || ""}`}
                      </Tag>
                    )}
                    <IconButton
                      aria-label={
                        addressOpen
                          ? t("Collapse {{section}} section", { section: t("Address Information") })
                          : t("Expand {{section}} section", { section: t("Address Information") })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection('address');
                      }}
                      icon={addressOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
                <Collapse in={addressOpen} animateOpacity>
                  <Box mt={4} minH="365px">
                    {loading ? (
                      <Center minH="365px" w="100%">
                        <Spinner size="xl" />
                      </Center>
                    ) : (
                      <Address user={user} profile={profile} countries={countries} uploads={uploads} minH="365px" />
                    )}
                  </Box>
                </Collapse>
              </Card>

              {/* Business */}
              <Card>
                <Flex
                  align="center"
                  justify="space-between"
                  mb="2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={businessOpen}
                  onClick={() => toggleSection('business')}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection('business');
                    }
                  }}
                >
                  <Box>
                    <Heading size="md">{t("Business Information")}</Heading>
                    <Text fontSize="sm" color="gray.500" pt="1">
                      {t("Provide more information about your business")}
                    </Text>
                  </Box>
                  <Flex align="center" gap={2}>
                    {!businessOpen && hasScore && (
                      <Tag size="sm" colorScheme={scoreScheme} borderRadius="full" aria-label={t("Profile Score")}>
                        {`${scoreValue}${scoreUnit || ""}`}
                      </Tag>
                    )}
                    <IconButton
                      aria-label={
                        businessOpen
                          ? t("Collapse {{section}} section", { section: t("Business Information") })
                          : t("Expand {{section}} section", { section: t("Business Information") })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection('business');
                      }}
                      icon={businessOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
                <Collapse in={businessOpen} animateOpacity>
                  <Box mt={4} minH="365px">
                    {loading ? (
                      <Center minH="365px" w="100%">
                        <Spinner size="xl" />
                      </Center>
                    ) : (
                      <Business user={user} profile={profile} countries={countries} uploads={uploads} developer={developer} minH="365px" />
                    )}
                  </Box>
                </Collapse>
              </Card>

              {/* Account Settings */}
              <Card mt="20px">
                <Flex
                  align="center"
                  justify="space-between"
                  mb="2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={accountOpen}
                  onClick={() => toggleSection('account')}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection('account');
                    }
                  }}
                >
                  <Box>
                    <Heading size="md">{t("Account Settings")}</Heading>
                    <Text fontSize="sm" color="gray.500" pt="1">
                      {t("Manage your account settings")}
                    </Text>
                  </Box>
                  <Flex align="center" gap={2}>
                    <IconButton
                      aria-label={
                        accountOpen
                          ? t("Collapse {{section}} section", { section: t("Account Settings") })
                          : t("Expand {{section}} section", { section: t("Account Settings") })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection('account');
                      }}
                      icon={accountOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                    />
                  </Flex>
                </Flex>
                <Collapse in={accountOpen} animateOpacity>
                  <Box mt={4} minH="365px">
                    {loading ? (
                      <Center minH="365px" w="100%">
                        <Spinner size="xl" />
                      </Center>
                    ) : (
                      <Settings uploads={uploads} developer={developer} minH="365px" />
                    )}
                  </Box>
                </Collapse>
              </Card>
            </Box>

            {/* Right column: File Uploader */}
            <Box gridColumn={{ base: "auto", lg: "2 / 3" }} alignSelf="start" maxW="100%" w="100%" minW={0}>
              <Card w="100%">
                <Flex
                  align="center"
                  justify="space-between"
                  mb="2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={uploadOpen}
                  onClick={() => toggleSection('upload')}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleSection('upload');
                    }
                  }}
                >
                  <Box>
                    <Heading size="md">{t("File Uploader")}</Heading>
                    <Text fontSize="sm" color="gray.500" pt="1">
                      {t("This tool allows you upload files, identity documents, images and others from your device to BlockMap.")}
                    </Text>
                  </Box>
                  <IconButton
                    aria-label={
                      uploadOpen
                        ? t("Collapse {{section}} section", { section: t("File Uploader") })
                        : t("Expand {{section}} section", { section: t("File Uploader") })
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection('upload');
                    }}
                    icon={uploadOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    size="sm"
                    variant="ghost"
                  />
                </Flex>
                <Collapse in={uploadOpen} animateOpacity>
                  <Box mt={4}>
                    <Upload
                      uploads={uploads}
                      onChange={(e) => {
                        uploads.push(e);
                        setUploads(uploads);
                      }}
                      minH={{ base: "auto", lg: "500px", "2xl": "365px" }}
                      pb={{ base: "100px", lg: "20px" }}
                    />
                  </Box>
                </Collapse>
              </Card>
            </Box>
          </Grid>
        </>
      )}
    </Box>
  );

  // Get countries
  function getCountries() {
    setLoading(true);
    authentication.getCountries(null, (error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast("error", "FAILED", error?.message || "Snap! Something went wrong, Try again");
      } else {
        const results = (data || [])
          .map((d) => d.country_iso_code_2)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setCountries(results);
        getUser();
      }
    });
  }

  // Get user details
  function getUser() {
    setLoading(true);
    authentication.getUser((error, data) => {
      setLoading(false);
      if (error) {
        constants.showToast("error", "FAILED", error?.message || "Snap! Something went wrong, Try again");
      } else {
        setUser(data?.user || {});
        setProfile(data?.profile || {});
        setProfileScore(data?.profile_score || {});
        setDeveloper(data?.developer || {});
        setToken(data?.token || {});
        isContactInfoValidated(data?.user || {}, data?.profile || {}, data?.developer || {});
      }
    });
  }

  function isContactInfoValidated(user, profile, developer) {
    const newNotifications = new Set();
    if (!user?.email_verified) {
      newNotifications.add(t("You need to verify your email address! Your access to some APIs might be limited"));
    }
    if (!user?.phone_verified) {
      newNotifications.add(t("You need to verify your phone number! Your access to some APIs might be limited"));
    }
    if (!profile?.document_upload_verified) {
      newNotifications.add(t("You need to submit your identity document(s) for verification! Your access to some APIs might be limited"));
    }
    if (!profile?.address_upload_verified) {
      newNotifications.add(t("You need to submit your proof of address document(s) for verification! Your access to some APIs might be limited"));
    }
    if (!profile?.business_upload_verified) {
      newNotifications.add(t("You need to submit your business document(s) for verification! Your access to some APIs might be limited"));
    }
    if (user?.country_shortcode === "NG" && !profile?.bvn) {
      newNotifications.add(t("A Bank Verification Number (BVN) is required for Nigerian developers, Kindly update your profile"));
    }
    if (user?.country_shortcode === "NG" && !profile?.bvn_verified) {
      newNotifications.add(t("Your Bank Verification Number (BVN) is not verified. Kindly update your profile"));
    }
    if (!developer?.webhook_url) {
      newNotifications.add(t("You need to set a Webhook URL to receive transaction notifications"));
    }
    if (!developer?.enable_webhooks) {
      newNotifications.add(t("You need to enable Webhooks to receive transaction notifications"));
    }
    setNotifications([...newNotifications]);
  }
}
